/**
 * Anomaly Detection Panel Component
 *
 * DC-706: Displays outlier/anomaly analysis results using IQR, Z-Score,
 * Modified Z-Score methods and Benford's Law test.
 *
 * @see Documentation/Development/Design Docs/Missing_Accordion_Capabilities_Design.md
 */

import React, { useState, useRef } from 'react';
import './anomaly-detection-panel.css';
import type { DQBenfordsLawResult } from '../../types/dataQualityMetrics';

// Types matching server/dataQuality/outliers.ts
type OutlierInterpretation = 'none' | 'few' | 'moderate' | 'many' | 'excessive';

interface OutlierResult {
  method: 'iqr' | 'zscore' | 'modified_zscore';
  outlier_indices: number[];
  outlier_count: number;
  outlier_percentage: number;
  lower_bound: number;
  upper_bound: number;
  interpretation: OutlierInterpretation;
  recommendation: string;
}

interface ColumnOutlierSummary {
  column: string;
  iqr_outliers: OutlierResult;
  zscore_outliers: OutlierResult;
  consensus_outliers: number[];
  recommendation: string;
}

// v2.5 SS-01 (A2): the renderer consumes the CLIENT MIRROR of the server's
// normalized read-model contract — no local redeclaration to drift.
type BenfordsLawResult = DQBenfordsLawResult;

interface AnomalyReport {
  analyzedAt: string;
  columnsAnalyzed: number;
  columnsWithAnomalies: number;
  totalAnomalies: number;
  columnResults: ColumnOutlierSummary[];
  benfordResults: BenfordsLawResult[];
  overallSeverity: 'none' | 'low' | 'medium' | 'high';
  summary: string;
}

interface AnomalyDetectionPanelProps {
  journeyId?: string;
  onAnalysisComplete?: (report: AnomalyReport) => void;
}

const AnomalyDetectionPanel: React.FC<AnomalyDetectionPanelProps> = ({
  journeyId: _journeyId,
  onAnalysisComplete
}) => {
  const [report, setReport] = useState<AnomalyReport | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedColumn, setExpandedColumn] = useState<string | null>(null);
  const [expandedBenford, setExpandedBenford] = useState<string | null>(null);
  const [includeBenford, setIncludeBenford] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleAnalyze = async (file: File) => {
    try {
      setAnalyzing(true);
      setError(null);

      const formData = new FormData();
      formData.append('file', file);
      formData.append('include_benford', String(includeBenford));

      const response = await fetch('/api/data-quality/anomaly/detect', {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to detect anomalies');
      }

      setReport(data.report);
      onAnalysisComplete?.(data.report);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to analyze');
    } finally {
      setAnalyzing(false);
    }
  };

  const getSeverityClass = (severity: AnomalyReport['overallSeverity']): string => {
    return `anomaly-severity--${severity}`;
  };

  const getInterpretationClass = (interp: OutlierInterpretation): string => {
    switch (interp) {
      case 'none': return 'anomaly-severity--none';
      case 'few': return 'anomaly-severity--low';
      case 'moderate': return 'anomaly-severity--medium';
      case 'many':
      case 'excessive': return 'anomaly-severity--high';
      default: return '';
    }
  };

  return (
    <div className="anomaly-panel">
      {/* Header */}
      <div className="anomaly-panel__header">
        <div className="anomaly-panel__title">
          <h3>Anomaly Detection</h3>
          {report && (
            <span className={`anomaly-panel__severity-badge ${getSeverityClass(report.overallSeverity)}`}>
              {report.overallSeverity.toUpperCase()}
            </span>
          )}
        </div>
        <div className="anomaly-panel__actions">
          <label className="anomaly-panel__checkbox">
            <input
              type="checkbox"
              checked={includeBenford}
              onChange={(e) => setIncludeBenford(e.target.checked)}
            />
            <span>Include Benford's Law</span>
          </label>
          <button
            className="anomaly-panel__btn anomaly-panel__btn--primary"
            onClick={() => fileInputRef.current?.click()}
            disabled={analyzing}
          >
            {analyzing ? 'Analyzing...' : 'Detect Anomalies'}
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="anomaly-panel__error">
          <span>{error}</span>
          <button onClick={() => setError(null)}>x</button>
        </div>
      )}

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept=".csv,.tsv,.xlsx,.xls"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleAnalyze(file);
          e.target.value = '';
        }}
      />

      {/* Loading State */}
      {analyzing && (
        <div className="anomaly-panel__loading">
          <div className="spinner" />
          <span>Analyzing data for anomalies...</span>
        </div>
      )}

      {/* No Analysis Yet */}
      {!report && !analyzing && (
        <div className="anomaly-panel__empty">
          <div className="anomaly-panel__empty-icon">🔍</div>
          <h4>Detect Data Anomalies</h4>
          <p>Upload a dataset to detect outliers using IQR, Z-Score, and Benford's Law analysis.</p>
        </div>
      )}

      {/* Results Display */}
      {report && (
        <div className="anomaly-panel__results">
          {/* Summary */}
          <div className="anomaly-panel__summary">
            <div className="anomaly-panel__score">
              <div className={`anomaly-panel__score-value ${getSeverityClass(report.overallSeverity)}`}>
                {report.totalAnomalies}
              </div>
              <div className="anomaly-panel__score-label">Total Outliers</div>
            </div>
            <div className="anomaly-panel__stats">
              <div className="anomaly-panel__stat">
                <span className="anomaly-panel__stat-value">{report.columnsAnalyzed}</span>
                <span className="anomaly-panel__stat-label">Columns Analyzed</span>
              </div>
              <div className="anomaly-panel__stat">
                <span className={`anomaly-panel__stat-value ${report.columnsWithAnomalies > 0 ? 'anomaly-severity--medium' : ''}`}>
                  {report.columnsWithAnomalies}
                </span>
                <span className="anomaly-panel__stat-label">With Anomalies</span>
              </div>
              {report.benfordResults.length > 0 && (
                <div className="anomaly-panel__stat">
                  {/* v2.5 SS-01: a deviation requires passes_test === false —
                      not-assessable entries (null) are neither pass nor fail. */}
                  <span className={`anomaly-panel__stat-value ${
                    report.benfordResults.filter(r => r.passes_test === false).length > 0 ? 'anomaly-severity--medium' : ''
                  }`}>
                    {report.benfordResults.filter(r => r.passes_test === false).length}
                  </span>
                  <span className="anomaly-panel__stat-label">Benford Deviations</span>
                </div>
              )}
            </div>
          </div>

          {/* Summary Text */}
          <div className="anomaly-panel__summary-text">
            {report.summary}
          </div>

          {/* Outlier Results */}
          {report.columnResults.length > 0 && (
            <div className="anomaly-panel__section">
              <h4>Outlier Analysis by Column</h4>
              <div className="anomaly-panel__column-list">
                {report.columnResults.map((col) => (
                  <div
                    key={col.column}
                    className={`anomaly-panel__column ${expandedColumn === col.column ? 'anomaly-panel__column--expanded' : ''}`}
                  >
                    <div
                      className="anomaly-panel__column-header"
                      onClick={() => setExpandedColumn(
                        expandedColumn === col.column ? null : col.column
                      )}
                    >
                      <div className="anomaly-panel__column-info">
                        <span className="anomaly-panel__column-name">{col.column}</span>
                        <span className="anomaly-panel__consensus-badge">
                          {col.consensus_outliers.length} consensus
                        </span>
                      </div>
                      <div className="anomaly-panel__column-status">
                        <span className={`anomaly-panel__method-badge ${getInterpretationClass(col.iqr_outliers.interpretation)}`}>
                          IQR: {col.iqr_outliers.outlier_count}
                        </span>
                        <span className={`anomaly-panel__method-badge ${getInterpretationClass(col.zscore_outliers.interpretation)}`}>
                          Z: {col.zscore_outliers.outlier_count}
                        </span>
                        <span className="anomaly-panel__expand-icon">
                          {expandedColumn === col.column ? '▼' : '▶'}
                        </span>
                      </div>
                    </div>

                    {expandedColumn === col.column && (
                      <div className="anomaly-panel__column-details">
                        {/* IQR Method */}
                        <div className="anomaly-panel__method">
                          <div className="anomaly-panel__method-header">
                            <span className="anomaly-panel__method-name">IQR Method</span>
                            <span className="anomaly-panel__method-value">
                              {col.iqr_outliers.outlier_percentage.toFixed(1)}%
                            </span>
                          </div>
                          <div className="anomaly-panel__bounds">
                            Lower: {col.iqr_outliers.lower_bound.toFixed(2)} |
                            Upper: {col.iqr_outliers.upper_bound.toFixed(2)}
                          </div>
                        </div>

                        {/* Z-Score Method */}
                        <div className="anomaly-panel__method">
                          <div className="anomaly-panel__method-header">
                            <span className="anomaly-panel__method-name">Z-Score Method</span>
                            <span className="anomaly-panel__method-value">
                              {col.zscore_outliers.outlier_percentage.toFixed(1)}%
                            </span>
                          </div>
                          <div className="anomaly-panel__bounds">
                            Lower: {col.zscore_outliers.lower_bound.toFixed(2)} |
                            Upper: {col.zscore_outliers.upper_bound.toFixed(2)}
                          </div>
                        </div>

                        {/* Recommendation */}
                        <div className="anomaly-panel__recommendation">
                          <strong>Recommendation:</strong> {col.recommendation}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Benford's Law Results */}
          {report.benfordResults.length > 0 && (
            <div className="anomaly-panel__section">
              <h4>Benford's Law Analysis</h4>
              <p className="anomaly-panel__section-desc">
                Compares each column's first-digit distribution against the Benford reference. A deviation is a review prompt — check whether Benford analysis applies to the field before investigating further.
              </p>
              <div className="anomaly-panel__benford-list">
                {report.benfordResults.map((result) => (
                  <div
                    key={result.column}
                    className={`anomaly-panel__benford ${expandedBenford === result.column ? 'anomaly-panel__benford--expanded' : ''}`}
                  >
                    <div
                      className="anomaly-panel__benford-header"
                      onClick={() => setExpandedBenford(
                        expandedBenford === result.column ? null : result.column
                      )}
                    >
                      <div className="anomaly-panel__benford-info">
                        <span className="anomaly-panel__benford-name">{result.column}</span>
                        <span className="anomaly-panel__benford-sample">n={result.sample_size}</span>
                      </div>
                      <div className="anomaly-panel__benford-status">
                        <span className={`anomaly-panel__benford-badge ${
                          result.not_assessable || result.passes_test === null
                            ? 'anomaly-severity--muted'
                            : result.passes_test
                              ? 'anomaly-severity--none'
                              : 'anomaly-severity--medium'
                        }`}>
                          {result.not_assessable || result.passes_test === null
                            ? 'NOT ASSESSABLE'
                            : result.passes_test
                              ? 'CONSISTENT'
                              : 'DIFFERS FROM REFERENCE'}
                        </span>
                        <span className="anomaly-panel__expand-icon">
                          {expandedBenford === result.column ? '▼' : '▶'}
                        </span>
                      </div>
                    </div>

                    {expandedBenford === result.column && (
                      <div className="anomaly-panel__benford-details">
                        {/* v2.5 SS-01: invalid values are EXCLUDED from display —
                            a not-assessable entry shows no χ²/p statistics. */}
                        <div className="anomaly-panel__benford-stats">
                          {!(result.not_assessable === true || result.passes_test === null) && (
                            <>
                              <span>Chi-square: {result.chi_square.toFixed(2)}</span>
                              <span>p-value: {result.p_value.toFixed(4)}</span>
                            </>
                          )}
                          {result.legacy === true && (
                            <span className="anomaly-panel__benford-legacy">▫ Earlier calculation version — re-normalized for display</span>
                          )}
                        </div>
                        {result.display?.headline && (
                          <div className="anomaly-panel__benford-headline">{result.display.headline}</div>
                        )}

                        {/* Digit Distribution Chart — omitted for not-assessable
                            entries (their evidence failed validation) */}
                        {!(result.not_assessable || !result.first_digit_distribution) && (
                        <div className="anomaly-panel__digit-chart">
                          <div className="anomaly-panel__digit-header">
                            <span>Digit</span>
                            <span>Observed</span>
                            <span>Expected</span>
                          </div>
                          {Object.keys(result.first_digit_distribution ?? {}).sort().map((digit) => {
                            // v2.5 SS-01 (A2): BOTH columns renormalize by their own
                            // sums — the old `expected * 100` assumed fractions and
                            // rendered count-valued legacy payloads as >100% values.
                            const obsDist = result.first_digit_distribution ?? {};
                            const expDist = result.expected_distribution ?? {};
                            const observed = obsDist[digit] || 0;
                            const expected = expDist[digit] || 0;
                            const obsTotal = Object.values(obsDist).reduce((a, b) => a + b, 0) || 1;
                            const expTotal = Object.values(expDist).reduce((a, b) => a + b, 0) || 1;
                            const observedPct = (observed / obsTotal) * 100;
                            const expectedPct = (expected / expTotal) * 100;
                            return (
                              <div key={digit} className="anomaly-panel__digit-row">
                                <span className="anomaly-panel__digit-num">{digit}</span>
                                <div className="anomaly-panel__digit-bars">
                                  <div
                                    className="anomaly-panel__digit-bar anomaly-panel__digit-bar--observed"
                                    style={{ width: `${observedPct * 2}%` }}
                                    title={`Observed: ${observedPct.toFixed(1)}%`}
                                  />
                                  <div
                                    className="anomaly-panel__digit-bar anomaly-panel__digit-bar--expected"
                                    style={{ width: `${expectedPct * 2}%` }}
                                    title={`Expected: ${expectedPct.toFixed(1)}%`}
                                  />
                                </div>
                                <span className="anomaly-panel__digit-pct">{observedPct.toFixed(1)}%</span>
                              </div>
                            );
                          })}
                        </div>
                        )}

                        <div className="anomaly-panel__recommendation">
                          {result.recommendation}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="anomaly-panel__footer">
            <span>Analyzed: {new Date(report.analyzedAt).toLocaleString()}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnomalyDetectionPanel;
